import {useMemo} from 'react';

import MoGreenModakPhone from '@assets/animations/mo/moGreenModakPhone.json';
import {Box, Typography} from '@mui/material';
import {AnimatePresence, motion} from 'framer-motion';
import Lottie from 'lottie-react';
import {useTranslation} from 'react-i18next';

import FuseSvgIcon from '@/@fuse/core/FuseSvgIcon';
import {
  AppButton,
  AppButtonVariant,
  MessageBubble,
  SmartBanner,
} from '@/app/shared-components';
import {
  EMessageBubbleSizeVariant,
  EMessageBubbleTailVariant,
  EMessageBubbleToneVariant,
} from '@/app/shared-components/MessageBubble/enums';
import {COLORS} from '@/styles/colors';

import {
  ANIMATION,
  EXIT_ANIMATION,
  INITIAL_ANIMATION,
  TRANSITION_ANIMATION,
} from './models/constants';
import {useViewModelProvider, ViewModelProvider} from './ViewModelContext';

import './styles';

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

const FeatureItem = ({icon, label}: {icon: string; label: string}) => (
  <Box className="download-app-info-container">
    <Box className="info-icon">
      <img
        src={icon}
        alt="feature icon"
      />
    </Box>
    <Typography className="download-app-text-title">{label}</Typography>
  </Box>
);

const appleLogo = isIOS ? (
  <img
    src="/assets/images/logo/apple_logo.webp"
    width={16}
    height={16}
    alt="Apple icon"
  />
) : null;

const arrowUpRightIcon = (
  <FuseSvgIcon
    size={18}
    sx={{color: COLORS.ON_SURFACE}}>
    heroicons-outline:arrow-up-right
  </FuseSvgIcon>
);

const SmartBannerFooter = ({
  isMobile,
  isFromLogin,
  onDownload,
  onRegister,
  t,
}: {
  isMobile: boolean;
  isFromLogin: boolean;
  onDownload: () => void;
  onRegister: () => void;
  t: (key: string) => string;
}) => {
  if (!isMobile) return null;

  return isFromLogin ? (
    <Box
      display={'flex'}
      flexDirection={'column'}
      gap={1}>
      <AppButton
        label={t('download_app_page.from_login.smart_banner_button')}
        onClick={onDownload}
        iconLeft={appleLogo}
        iconRight={arrowUpRightIcon}
        variant={AppButtonVariant.onPrimary}
        color={COLORS.WHITE}
        labelColor={COLORS.ON_SURFACE}
      />

      <AppButton
        label={t('download_app_page.from_login.smart_banner_secondary_button')}
        onClick={onRegister}
        iconRight={arrowUpRightIcon}
        variant={AppButtonVariant.onPrimary}
        color={COLORS.ON_SURFACE}
        labelColor={COLORS.WHITE}
      />
    </Box>
  ) : (
    <AppButton
      label={t('download_app_page.smart_banner_button')}
      onClick={onDownload}
      iconLeft={appleLogo}
      iconRight={arrowUpRightIcon}
      variant={AppButtonVariant.onPrimary}
      color={COLORS.WHITE}
      labelColor={COLORS.ON_SURFACE}
    />
  );
};

export const DownloadAppContent = () => {
  const {t} = useTranslation();
  const {
    isMobile,
    downloadImage,
    titleLabel,
    featureItems,
    isFromLogin,
    isParent,
    isNewParentOnboarding,
    showBanner,
    qrDownloadImage,
    showMoGreenLottie,
    messageBubbleText,
    parentSubtitle,
    coParentKidsNames,
    handleOpenDownloadApp,
    handleOpenModakRegister,
    handleOpenModakMbxPolicy,
  } = useViewModelProvider();

  const smartBannerFooter = useMemo(
    () => (
      <SmartBannerFooter
        isMobile={isMobile}
        isFromLogin={isFromLogin}
        onDownload={handleOpenDownloadApp}
        onRegister={handleOpenModakRegister}
        t={t}
      />
    ),
    [handleOpenDownloadApp, handleOpenModakRegister, isFromLogin, isMobile, t],
  );

  const smartBanner = useMemo(
    () => (
      <SmartBanner
        srcImage="/assets/images/logo/smart-banner-modak-logo.webp"
        title={t('download_app_page.smart_banner.title')}
        description={t('download_app_page.smart_banner.description')}
        textReview={t('download_app_page.smart_banner.textReview')}
        iconReview="/assets/images/pages/yellow_stars.webp"
        footer={smartBannerFooter}
      />
    ),
    [smartBannerFooter, t],
  );

  const banner = useMemo(() => {
    return isMobile ? (
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={INITIAL_ANIMATION}
            animate={ANIMATION}
            exit={EXIT_ANIMATION}
            transition={TRANSITION_ANIMATION}
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
            }}>
            {smartBanner}
          </motion.div>
        )}
      </AnimatePresence>
    ) : (
      smartBanner
    );
  }, [isMobile, showBanner, smartBanner]);

  return (
    <Box className="download-app-page-container">
      {(isFromLogin || !isParent) && (
        <Box className="download-app-modak-logo-container">
          <img
            src={'/assets/icons/modak-logo-grey.svg'}
            width={114}
            height={24}
            alt="Modak grey logo"
          />
        </Box>
      )}

      <Box
        data-testid="download-app-title-container"
        className={`download-app-title-container`}>
        <Typography
          data-testid="download-app-title"
          className="download-app-title">
          {titleLabel}
        </Typography>

        {!coParentKidsNames &&
          (isNewParentOnboarding ? (
            <Typography className="download-app-subtitle">
              {parentSubtitle}
            </Typography>
          ) : (
            <Box
              className={`download-app-list-description ${isParent || isFromLogin ? 'icon-arrow' : ''}`}>
              {featureItems.map(({icon, label}) => (
                <FeatureItem
                  key={label}
                  icon={icon}
                  label={label}
                />
              ))}
            </Box>
          ))}
      </Box>

      <Box className="download-app-image-container">
        {!isMobile && (
          <img
            src={qrDownloadImage.source}
            width={qrDownloadImage.width}
            height={qrDownloadImage.height}
            alt="Download App QR"
          />
        )}
        <Box className="download-app-image-container-content">
          {messageBubbleText && (
            <MessageBubble
              className="download-app-message-bubble"
              text={messageBubbleText}
              tailVariant={EMessageBubbleTailVariant.Triangle}
              sizeVariant={EMessageBubbleSizeVariant.Md}
              toneVariant={EMessageBubbleToneVariant.Elevated}
            />
          )}
          {showMoGreenLottie ? (
            <Lottie
              animationData={MoGreenModakPhone}
              loop={true}
            />
          ) : (
            <Box>
              <img
                src={downloadImage.source}
                width={downloadImage.width}
                height={downloadImage.height}
                className="download-app-image"
                alt="Download App Phone"
              />
            </Box>
          )}
        </Box>
      </Box>

      {banner}
      {isFromLogin && !isMobile && (
        <Typography className="download-app-sign-up-app-text">
          {t('download_app_page.not_have_account')}{' '}
          <span
            className="download-app-sign-in-text"
            onClick={handleOpenModakRegister}>
            {t('download_app_page.sign_up')}
          </span>
        </Typography>
      )}

      <Box className="download-app-disclaimers">
        {!isFromLogin && !isParent && (
          <Typography className="download-app-disclaimer-text">
            {t('download_app_page.kid_disclaimer')}{' '}
            <span
              className="download-app-disclaimer-text-link"
              onClick={handleOpenModakMbxPolicy}>
              {t('download_app_page.kid_disclaimer_link')}
            </span>
          </Typography>
        )}

        <Typography className="download-app-disclaimer-text">
          {t('download_app_page.common_1_disclaimer')}{' '}
          {t('download_app_page.common_2_disclaimer')}
        </Typography>

        <Typography className="download-app-disclaimer-text">
          {t('download_app_page.common_3_disclaimer')}
        </Typography>
      </Box>
    </Box>
  );
};

const DownloadAppPage = () => (
  <ViewModelProvider>
    <DownloadAppContent />
  </ViewModelProvider>
);

export default DownloadAppPage;
